import { Invoice, InvoiceSummary } from '@patient-ui/shared/models';

/**
 * Resolving invoice NUMBERS to the portal's own invoices, and splitting the
 * result into the primary bill and the rest.
 *
 * Two callers need exactly this, and they must agree: the Orbi billing hand-off
 * (PCSA-13559), which is handed numbers by the agent, and the Update Insurance
 * page's own arrival path (PCSA-13312), which is handed numbers by its URL when a
 * refresh has emptied the store. If those two resolved differently, the page a
 * patient reloaded would be about a different bill from the one they were sent to.
 */

/**
 * What the Update Insurance page's "additional invoices" control accepts in total.
 * Its own validator is `maxLength(98)` over the comma-joined string, so this is
 * the budget anything filling it has to stay inside.
 */
export const INVOICE_ADDITIONAL_NUMBERS_MAX_LENGTH = 98;

/**
 * What that control accepts per entry. Its own pattern is `/^[ 0-9, ]*$/` over the
 * whole joined string; per invoice number that leaves plain digits, since a comma
 * or space inside one would read as a separator.
 */
const NUMERIC_INVOICE = /^[0-9]+$/;

/** Trim + upper, the one normalization an invoice-number comparison may apply. */
function invoiceKey(value: string | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

/**
 * The invoices named by `invoiceNumbers`, in the order they were named.
 *
 * Open bills arrive in three buckets (collections / current / past due) — the same
 * flattening the Billing page does — and only an exact invoice-number match is
 * carried. A number that resolves to nothing is dropped rather than approximated:
 * paying or insuring the wrong bill is worse than being asked to pick.
 *
 * "Exact" ignores surrounding whitespace and case only. Both sides of this
 * comparison come from the same backend but not by the same route — the agent
 * echoes the number it was asked about, a URL carries whatever was put in it, and
 * the summary carries what the invoice API returned — and the Billing page's own
 * list compares these numbers `.trim()`ed, so padding does occur in this data.
 * Digits are never reinterpreted (no leading-zero stripping): `00742795` and
 * `742795` stay distinct, because they could be two different bills.
 */
export function matchInvoicesByNumber(
  summary: InvoiceSummary | undefined,
  invoiceNumbers: readonly string[]
): Invoice[] {
  const open = [
    ...(summary?.collectionsInvoices ?? []),
    ...(summary?.currentInvoices ?? []),
    ...(summary?.pastDueInvoices ?? []),
  ];
  return invoiceNumbers
    .map((number) => {
      const wanted = invoiceKey(number);
      return wanted
        ? open.find((invoice) => invoiceKey(invoice.number) === wanted)
        : undefined;
    })
    .filter((invoice): invoice is Invoice => !!invoice);
}

/**
 * The numbers for the Update Insurance page's "additional invoices" field: every
 * resolved invoice AFTER the first, which is the primary one the page displays and
 * submits.
 *
 * The field is a free-text control the page fills from `state.selectedInvoiceList`
 * (`.toString()`, so comma-joined) and it carries real validators. A human ticking
 * checkboxes in the Billing modal cannot realistically overflow either of them,
 * but an agent can name any number of bills and a URL can be edited — and an
 * over-long or non-numeric value would land the patient on the page with an invalid
 * form they did not fill in and cannot clear, which blocks submit. So entries that
 * are not plain digits are dropped, and the list stops at the last number that fits
 * whole. Nothing is truncated mid-number: half an invoice number is worse than an
 * absent one.
 */
export function additionalInvoiceNumbers(
  invoices: readonly Invoice[]
): string[] {
  const carried: string[] = [];
  let length = 0;

  for (const invoice of invoices.slice(1)) {
    const number = (invoice.number ?? '').trim();
    if (!NUMERIC_INVOICE.test(number)) {
      continue;
    }
    // +1 for the comma this entry adds once it is not the first.
    const cost = carried.length ? number.length + 1 : number.length;
    if (length + cost > INVOICE_ADDITIONAL_NUMBERS_MAX_LENGTH) {
      break;
    }
    carried.push(number);
    length += cost;
  }

  return carried;
}

/**
 * The invoice numbers an `?invoices=` query param names, in URL order.
 *
 * The param is the page's ONLY memory of which bill it is about across a reload —
 * the store is in-memory, so a refresh empties `state.invoice` and the page would
 * otherwise eject to the Billing list. Blank entries are dropped so a trailing
 * comma cannot resolve to nothing and take the whole hand-off down with it.
 */
export function parseInvoiceNumbersParam(
  param: string | null | undefined
): string[] {
  return (param ?? '')
    .split(',')
    .map((number) => number.trim())
    .filter((number) => number.length > 0);
}
